import { useState, useEffect } from 'react';
import { NavLink, generatePath } from 'react-router-dom';
import {
  IconBlock,
  ButtonSecondary,
  ButtonSize,
  IconSelected,
  IconEdit,
  ToggleInput,
  ToggleSize,
  IconChangeLoClock,
} from '@ui/src';
import { BankRoutes } from '../../routes';
import { getPspPartnerType } from '../../hoc/bank-and-psp/helpers/get-enums-names';
import Card from '../partial/card/card';
import InfoRow from './info-row';
import {
  PspPartnerType,
  PspPartnerLocation,
  ExportFileTypeEnum,
  PspPartnerPaymentType,
} from '../../hoc/bank-and-psp/types';
import { PaymentType } from '../../store/dictionaries/types';
import { GeneralInfoTypes } from './types';

import cn from './info.module.css';

const getExportFileTypeName = (type: ExportFileTypeEnum) => {
  let exportFileTypeName: string;

  switch (type) {
    case ExportFileTypeEnum.HARBOUR_HILLS_B2B_LITE:
      exportFileTypeName = 'Harbour&Hills B2B Lite';
      break;
    default:
      exportFileTypeName = type;
      break;
  }

  return exportFileTypeName;
};

const GeneralInfo = ({
  isLoading,
  generalInfo,
  onStatusChange,
  isEditable,
  statusChangeInProgress,
  bankId,
  changeVisibility,
  visiblityChangeInProgress,
  deactivateAllowed,
  paymentTypes,
  auditLogAvailable,
  setChangeLogPanelIsOpen,
}: GeneralInfoTypes) => {
  const [filteredPaymentTypes, setFilteredPaymentTypes] = useState<
    PaymentType[]
  >([]);

  let buttonText;
  let buttonIcon;

  if (statusChangeInProgress) {
    buttonText = 'Loading...';
  } else {
    buttonText = `${generalInfo?.active ? 'Deactivate' : 'Activate'} Bank`;
  }

  if (generalInfo?.active) {
    buttonIcon = <IconBlock />;
  } else {
    buttonIcon = <IconSelected />;
  }

  useEffect(() => {
    if (paymentTypes && generalInfo?.paymentsTypes) {
      const nextPaymentTypes: PaymentType[] = [];

      paymentTypes.forEach((item) => {
        if (
          generalInfo?.paymentsTypes?.find((type) => item.paymentType === type)
        ) {
          nextPaymentTypes.push(item);
        }
      });

      setFilteredPaymentTypes(nextPaymentTypes);
    }
  }, [paymentTypes, generalInfo?.paymentsTypes]);

  return (
    <Card
      isLoading={isLoading}
      title={
        <>
          <div className={cn.generalInfoTitleWrapper}>
            <div className={cn.generalInfoTitleLeft}>
              <div className={cn.generalInfoTitle}>
                {generalInfo?.companyName}
              </div>
              <div
                className={`${cn.generalInfoStatus} ${
                  generalInfo?.active ? cn.generalInfoStatusActive : ''
                }`}
              >
                {generalInfo?.active ? <IconSelected /> : <IconBlock />}
                {generalInfo?.active ? 'Active' : 'Disabled'}
              </div>
            </div>
            <div className={cn.generalInfoTitleRight}>
              <div className={cn.visibleForClientsWrapper}>
                <ToggleInput
                  title="Visible to clients"
                  name="toggle-check"
                  size={ToggleSize.SMALL}
                  onChange={changeVisibility}
                  checked={generalInfo?.visibleToClients}
                  disabled={!isEditable || visiblityChangeInProgress}
                />
              </div>
            </div>
          </div>
          <div className={cn.generalInfoControls}>
            <div className={cn.generalInfoControlsLeft}>
              {deactivateAllowed && (
                <ButtonSecondary
                  size={ButtonSize.SMALL}
                  text={buttonText}
                  icon={!statusChangeInProgress ? buttonIcon : undefined}
                  className={cn.changeStatusButton}
                  onClick={() => onStatusChange(!generalInfo?.active)}
                  disabled={!generalInfo?.pspIntegrationId}
                />
              )}
              {deactivateAllowed && isEditable && (
                <div className={cn.separator} />
              )}
              {isEditable && (
                <NavLink
                  to={generatePath(BankRoutes.BANK_AND_PSP_PAGE_EDIT, {
                    bankId: bankId as string,
                  })}
                  className={cn.infoTitleLink}
                >
                  <span className={cn.infoTitleLinkIcon}>
                    <IconEdit width={15} height={15} />
                  </span>
                  <span className={cn.infoTitleLinkText}>Edit</span>
                </NavLink>
              )}
            </div>
            {auditLogAvailable && (
              <button
                type="button"
                onClick={() => setChangeLogPanelIsOpen(true)}
                className={cn.changeLogLinks}
              >
                <IconChangeLoClock />
                Change Log
              </button>
            )}
          </div>
        </>
      }
    >
      <>
        <div className={cn.generalInfoBlock}>
          <div className={cn.generalInfoBlockContent}>
            <InfoRow title="Legal Name" value={generalInfo?.legalName} />
            <InfoRow title="Country" value={generalInfo?.country.name} />
            <InfoRow title="Address" value={generalInfo?.address} />
            <InfoRow
              title="Type"
              value={getPspPartnerType(
                generalInfo?.partnerType as unknown as keyof typeof PspPartnerType,
              )}
            />
            <InfoRow
              title="Location"
              value={
                PspPartnerLocation[
                  generalInfo?.location as unknown as keyof typeof PspPartnerLocation
                ]
              }
            />
            {(generalInfo?.paymentsTypes?.includes(
              PspPartnerPaymentType.FEDWIRE,
            ) ||
              (!generalInfo?.paymentsTypes?.includes(
                PspPartnerPaymentType.FEDWIRE,
              ) &&
                generalInfo?.aba)) && (
              <InfoRow title="ABA" value={generalInfo?.aba || '-'} />
            )}
          </div>
          <div className={cn.generalInfoBlockContent}>
            {generalInfo?.swift && (
              <InfoRow title="BIC of the Bank" value={generalInfo?.swift} />
            )}
            {generalInfo?.sortCode && (
              <InfoRow title="Sort Code" value={generalInfo?.sortCode} />
            )}
            <InfoRow
              title="Beneficiary"
              value={generalInfo?.beneficiary || '-'}
            />
            {(generalInfo?.paymentsTypes?.includes(
              PspPartnerPaymentType.CRYPTO_ASSET_PAYMENT,
            ) ||
              (!generalInfo?.paymentsTypes?.includes(
                PspPartnerPaymentType.CRYPTO_ASSET_PAYMENT,
              ) &&
                generalInfo?.caspId)) && (
              <InfoRow title="CASP" value={generalInfo?.casp?.name || '-'} />
            )}
            <InfoRow
              title="Internet bank link"
              value={
                generalInfo?.onlineBankLink ? (
                  <a
                    href={generalInfo?.onlineBankLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn.internetBankLink}
                  >
                    {generalInfo?.onlineBankLink}
                  </a>
                ) : (
                  '-'
                )
              }
            />
            <InfoRow
              title="Name / contacts manager"
              value={generalInfo?.managerContacts}
            />
            <InfoRow
              title="Export File Type"
              value={
                generalInfo?.exportFileType
                  ? getExportFileTypeName(generalInfo?.exportFileType)
                  : '-'
              }
            />
          </div>
        </div>
        {(generalInfo?.accountOfCorrespondentWithIntermediaryBank ||
          generalInfo?.intermediaryBankName ||
          generalInfo?.intermediaryBankSwift) && (
          <div className={cn.generalInfoBlock}>
            <div className={cn.generalInfoBlockContent}>
              {generalInfo?.intermediaryBankName && (
                <InfoRow
                  title="Intermediary bank"
                  value={generalInfo?.intermediaryBankName}
                />
              )}
              {generalInfo?.intermediaryBankSwift && (
                <InfoRow
                  title="Intermediary bank Swift"
                  value={generalInfo?.intermediaryBankSwift}
                />
              )}
              {generalInfo?.accountOfCorrespondentWithIntermediaryBank && (
                <InfoRow
                  title="Account of Correspondent with Intermediary Bank"
                  value={
                    generalInfo?.accountOfCorrespondentWithIntermediaryBank
                  }
                />
              )}
            </div>
          </div>
        )}
        {generalInfo?.description && (
          <div className={cn.generalInfoBlock}>
            <InfoRow title="Description" value={generalInfo?.description} />
          </div>
        )}

        {generalInfo?.pspIntegrationId && (
          <div className={cn.generalInfoBlock}>
            <InfoRow
              title="Available Payment Types"
              value={
                <div className={cn.paymentTypesWrapper}>
                  {filteredPaymentTypes?.map((item) => (
                    <div className={cn.paymentType} key={item.paymentType}>
                      {item.name}
                    </div>
                  ))}
                </div>
              }
            />
          </div>
        )}
      </>
    </Card>
  );
};

export default GeneralInfo;
